// Setting up a new business (M7 plan D4): one question per screen, each skippable. The chart of
// accounts and categories are already in place (the AU template); the bank comes next, from Home.
import { type FormEvent, useState } from 'react';
import { type Settings, WHEN_INVOICED } from './accountant/GstSettings.js';
import type { Api } from './api.js';

interface AbnCheck {
  valid: boolean;
  details?: { name: string | null; active: boolean; gstFrom: string | null } | null;
}

export function Setup({ api, orgId, onDone }: { api: Api; orgId: string; onDone: () => void }) {
  const [step, setStep] = useState<'abn' | 'gst'>('abn');
  const [abn, setAbn] = useState('');
  const [found, setFound] = useState<AbnCheck['details']>(null);
  const [registered, setRegistered] = useState(true);
  const [basis, setBasis] = useState<Settings['gstBasis']>('cash');
  const [frequency, setFrequency] = useState<'quarterly' | 'monthly' | 'annual'>('quarterly');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function saveAbn(event: FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await fetchCheck(api, abn);
      if (!res.valid) {
        setError('That is not a valid ABN. Check the 11 digits.');
        return;
      }
      await api.send(orgId, 'PUT', '/business/abn', { abn });
      setFound(res.details ?? null);
      if (res.details) setRegistered(!!res.details.gstFrom);
      setStep('gst');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the ABN.');
    } finally {
      setBusy(false);
    }
  }

  async function saveGst(event: FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.send(orgId, 'PUT', '/tax/settings', {
        gstRegistered: registered,
        gstBasis: basis,
        basFrequency: frequency,
        paygMethod: 'none',
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the GST settings.');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'abn')
    return (
      <form className="stack bank-card" onSubmit={saveAbn}>
        <h2>Your ABN</h2>
        <label>
          Australian Business Number (11 digits)
          <input
            inputMode="numeric"
            value={abn}
            onChange={(e) => setAbn(e.target.value)}
            required
          />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="title-row">
          <button type="submit" disabled={busy}>
            Continue
          </button>
          <button type="button" className="link" onClick={() => setStep('gst')}>
            Skip for now
          </button>
        </div>
      </form>
    );
  return (
    <form className="stack bank-card" onSubmit={saveGst}>
      <h2>GST</h2>
      {found?.name && (
        <p className="muted">
          {found.name}:{' '}
          {found.gstFrom
            ? `registered for GST since ${found.gstFrom.slice(0, 10)}`
            : 'not registered for GST'}
          .
        </p>
      )}
      <label className="inline">
        <input
          type="checkbox"
          checked={registered}
          onChange={(e) => setRegistered(e.target.checked)}
        />
        Registered for GST
      </label>
      {registered && (
        <>
          <label>
            When do you count GST?
            <select
              value={basis}
              onChange={(e) => setBasis(e.target.value as Settings['gstBasis'])}
            >
              <option value="cash">When money changes hands (most small businesses)</option>
              <option value={WHEN_INVOICED}>When invoiced or billed</option>
            </select>
          </label>
          <label>
            How often do you lodge a BAS?
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as 'quarterly' | 'monthly' | 'annual')}
            >
              <option value="quarterly">Every quarter</option>
              <option value="monthly">Every month</option>
              <option value="annual">Once a year</option>
            </select>
          </label>
        </>
      )}
      {error && <p className="error">{error}</p>}
      <div className="title-row">
        <button type="submit" disabled={busy}>
          Finish
        </button>
        <button type="button" className="link" onClick={onDone}>
          Skip for now
        </button>
      </div>
    </form>
  );
}

async function fetchCheck(api: Api, abn: string): Promise<AbnCheck> {
  return api.raw<AbnCheck>(`/abn/${encodeURIComponent(abn.replace(/\s/g, ''))}`);
}
